import { useGetCurrencyConversion, useNFTMainData, useNFTMainTraitData, useReservoirTokensData } from '@pkg/hooks';
import { trimDecimals } from '@pkg/utils';

import { type NFTTraitsDataType } from '@app/pages/NFTDetailView/components/TraitsPanel/NFTTraits.data';

interface UseNFTTraitData {
    traits: NFTTraitsDataType;
}

export const useNFTTraitData = (): UseNFTTraitData => {
    const { data: baseData } = useNFTMainData();
    const { data: collectionData } = useReservoirTokensData();
    const { data: collectionTraitsData } = useNFTMainTraitData();
    const { data: conversionData } = useGetCurrencyConversion({ interval: true });
    const conversionValue = conversionData?.ethereum?.usd || 0;

    const traits = collectionData?.tokens?.[0]?.token?.attributes?.map((trait, index) => {
        const formattedTrait = {
            id: `${trait.key}-${index}`,
            name: trait.value || '',
            type: trait.key || '',
            traitCount: trait.tokenCount || 0,
            traitRarityPercentage: trimDecimals((trait.tokenCountPercent || 0) * 100),
            eth: trimDecimals(trait.floorAskPrice || 0, 4),
            fiat: trimDecimals((trait.floorAskPrice || 0) * conversionValue),
            lastSale: 0,
            lastSaleDate: '',
            increase: 0,
        };
        const traitSales = collectionTraitsData?.attributes.find((attr) => {
            return attr.value === trait.value && attr.key === trait.key;
        });

        if (traitSales) {
            const lastSale = traitSales.lastSells[0];
            const previousSale = traitSales.lastSells[1];
            if (lastSale) {
                formattedTrait.lastSale = trimDecimals(lastSale.value, 4);
                formattedTrait.lastSaleDate =
                    (lastSale.timestamp && new Date(parseFloat(`${lastSale.timestamp}`) * 1000).toISOString()) || '';
                if (previousSale) {
                    formattedTrait.increase = trimDecimals((previousSale.value * 100) / lastSale.value);
                }
            }
        }

        return formattedTrait;
    });

    if (!traits) {
        // Fallback traits
        const traits =
            baseData?.attributes?.map((trait, index) => ({
                id: `${trait.trait_type}-${index}`,
                name: trait.value || '',
                type: trait.trait_type || '',
                eth: 0,
                traitCount: 0,
                traitRarityPercentage: 0,
                fiat: 0,
                lastSaleDate: '',
                lastSale: 0,
                increase: 0,
            })) || [];

        return { traits };
    }

    // sort highest ETH first
    traits?.sort((a, b) => b.eth - a.eth);

    return { traits };
};
